// Affiliate Marketing Kit — copy-paste-ready promo content for each
// shareable destination. Tabs across destinations (ebooks first, then
// library + marketplace). Each tab shows: 3 tweets, 1 LinkedIn post,
// 1 email (subject + body), the tracked deep-link, and (for ebooks) a
// preview of the affiliate-branded OG card.
//
// Wired to GET /api/affiliate/marketing-kit which auto-mints a referral
// code if the user doesn't have one yet, so opening this tab is always
// productive even for brand-new affiliates.
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Sparkles, Twitter, Linkedin, Mail, Image as ImageIcon,
  ExternalLink, Loader2, Check, BookOpen, ShoppingBag, Library as LibraryIcon,
} from "lucide-react";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("af_token")}` });

// Icon hint per destination — small visual cue to scan the tab strip.
const ICON_BY_KEY = {
  ebook_start:   BookOpen,
  ebook_w2:      BookOpen,
  ebook_buyers:  BookOpen,
  ebook_restart: BookOpen,
  ebook_calls:   BookOpen,
  ebook_tcpa:    BookOpen,
  library:       LibraryIcon,
  marketplace:   ShoppingBag,
};

export default function AffiliateMarketingKit() {
  const [bundles, setBundles] = useState([]);
  const [active, setActive]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    axios.get(`${API}/affiliate/marketing-kit`, { headers: authHeader() })
      .then((r) => {
        setBundles(r.data.bundles || []);
        setRefCode(r.data.ref_code || "");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load marketing kit"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-6 flex items-center gap-3 text-sm text-[var(--ink-2)]"
               data-testid="marketing-kit-loading">
        <Loader2 className="size-4 animate-spin" /> Loading your marketing kit…
      </section>
    );
  }
  if (!bundles.length) return null;

  const current = bundles[active];

  return (
    <section className="space-y-4" data-testid="marketing-kit">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Sparkles className="size-5 text-[var(--primary)]" /> Marketing Kit
          </h2>
          <p className="text-xs text-[var(--ink-2)] mt-0.5">
            Copy-paste-ready tweets, LinkedIn posts, and emails for each shareable destination.
            Your ref code <strong>{refCode}</strong> is already baked into every URL.
          </p>
        </div>
      </header>

      {/* Destination tabs */}
      <div className="flex flex-wrap gap-2" data-testid="kit-tabs">
        {bundles.map((b, i) => {
          const Icon = ICON_BY_KEY[b.destination_key] || BookOpen;
          const activeNow = i === active;
          return (
            <button
              key={b.destination_key}
              onClick={() => setActive(i)}
              data-testid={`kit-tab-${b.destination_key}`}
              className={
                "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border transition " +
                (activeNow
                  ? "bg-[var(--ink-0)] text-[var(--panel)] border-[var(--ink-0)]"
                  : "bg-[var(--panel)] text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--primary)]/40")
              }
            >
              <Icon size={13} />
              {b.destination_path}
            </button>
          );
        })}
      </div>

      {/* Active bundle */}
      <div className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-5 space-y-5"
           data-testid={`kit-bundle-${current.destination_key}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg text-[var(--ink-0)]">{current.headline}</h3>
            <p className="text-xs text-[var(--ink-3)] mt-1">
              <strong>Audience:</strong> {current.audience}
            </p>
          </div>
          <a href={current.tracked_url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-xs text-[var(--primary)] hover:underline whitespace-nowrap"
             data-testid="kit-open-link">
            Preview <ExternalLink size={11} />
          </a>
        </div>

        {/* Tracked deep-link */}
        <UrlRow url={current.tracked_url} />

        {/* Affiliate-branded OG image preview (ebooks only) */}
        {current.og_image_url && (
          <div data-testid="kit-og-preview">
            <Label icon={ImageIcon} text="Social preview card (your name baked in)" />
            <div className="rounded-lg border border-[var(--line)] overflow-hidden bg-[var(--bg)]">
              <img
                src={`${API.replace(/\/api$/, "")}${current.og_image_url}`}
                alt="Affiliate-branded OG preview"
                className="w-full max-w-2xl"
                loading="lazy"
              />
            </div>
            <p className="text-[10px] text-[var(--ink-3)] mt-1.5">
              This image auto-attaches when you paste the link on Twitter, LinkedIn, iMessage, or any modern social platform.
            </p>
          </div>
        )}

        {/* Tweets */}
        <div>
          <Label icon={Twitter} text="Tweets (pick the one that fits your voice)" />
          <div className="space-y-2">
            {current.tweets.map((t, i) => (
              <CopyBlock key={i} text={t} testid={`kit-tweet-${current.destination_key}-${i}`}/>
            ))}
          </div>
        </div>

        {/* LinkedIn */}
        <div>
          <Label icon={Linkedin} text="LinkedIn post" />
          <CopyBlock text={current.linkedin} testid={`kit-linkedin-${current.destination_key}`}/>
        </div>

        {/* Email */}
        <div>
          <Label icon={Mail} text="Email — paste into Gmail / Outlook / your newsletter" />
          <CopyBlock
            label="Subject"
            text={current.email_subject}
            single
            testid={`kit-email-subject-${current.destination_key}`}
          />
          <div className="h-2" />
          <CopyBlock
            label="Body"
            text={current.email_body}
            testid={`kit-email-body-${current.destination_key}`}
          />
        </div>
      </div>
    </section>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function Label({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-bold mb-2">
      <Icon size={12} /> {text}
    </div>
  );
}

function UrlRow({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select manually");
    }
  };
  return (
    <div className="flex items-center gap-2 bg-[var(--bg)] border border-[var(--line)] rounded-lg px-3 py-2"
         data-testid="kit-url-row">
      <code className="text-xs text-[var(--ink-0)] truncate flex-1">{url}</code>
      <button onClick={copy}
              className={
                "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-md border transition " +
                (copied
                  ? "border-[var(--primary)] text-[var(--primary)]"
                  : "border-[var(--line)] hover:border-[var(--primary)]/40 text-[var(--ink-1)]")
              }
              data-testid="kit-url-copy">
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

function CopyBlock({ text, label, single = false, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label ? `${label} copied` : "Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select manually");
    }
  };
  return (
    <div className="rounded-lg border border-[var(--line)] bg-[var(--bg)] hover:border-[var(--primary)]/30 transition"
         data-testid={testid}>
      <div className="flex items-start justify-between gap-2 px-3 pt-2.5 pb-1.5">
        {label && <span className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold">{label}</span>}
        {!label && <span />}
        <button onClick={copy}
                className={
                  "inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border transition " +
                  (copied
                    ? "border-[var(--primary)] text-[var(--primary)]"
                    : "border-[var(--line)] hover:border-[var(--primary)]/40 text-[var(--ink-1)]")
                }>
          {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
        </button>
      </div>
      <pre className={"px-3 pb-3 text-sm text-[var(--ink-0)] whitespace-pre-wrap " + (single ? "" : "leading-relaxed")}
           style={{ fontFamily: "inherit" }}>
        {text}
      </pre>
    </div>
  );
}
